/* System defined core library */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { LoaderService } from '../../../shared/services/loader.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { TimerService } from '../../../shared/services/timer.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { MessagingApiService } from '../../shared/services/messaging-api.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { PaymentApiService } from '../../shared/services/payment-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { EmailResponse } from '../../../shared/models/common/shared.model';
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { CardDetail } from '../../../shared/models/scheduling/carddetail.model';
import { EligibilityDetail } from '../../../shared/models/scheduling/eligibility.model';
import { MetricDetails, MetricRequest } from '../../../shared/models/scheduling/metrics.model';
import { CreatePaymentSessionRequest, CreatePaymentSessionResponse } from '../../../shared/models/scheduling/paymentSession.model';
import { ReminderDetails } from '../../../shared/models/scheduling/reminderdetail.model';
import { Session } from '../../../shared/models/scheduling/session.model';
import { Site } from '../../../shared/models/scheduling/site.model';

/* User defined enums/constants */
import { LanguageCultureCodes, SchedulingErrors } from '../../../shared/constants/scheduling.constant';
import { CardFee, ComponentType, Fee, LogLevel, NavigationAction } from '../../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html'
})
export class PaymentComponent implements OnInit {

  @HostListener('window:popstate', ['$event'])
  onPopState(event: any) {
    this.selectedExamDetailService.postedErrorMessage = null;

    this.metricApiService.logNavigation('Payment', NavigationAction.backwardsNavigation);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: any) {
    if (this.isBookingInProgress) {
      let holdId = '';
      let confNum = '';
      if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        let session = this.selectedAppointments[0].exam.sessions.find(sess => {
          return sess.selected;
        });
        holdId = session.hold.id;
        confNum = session.confirmationNumber;
      } else {
        holdId = this.selectedAppointments[0].hold.id;
        confNum = this.selectedAppointments[0].ConfirmationNumber;
      }

      this.metricApiService.log(LogLevel.Debug, `Candidate unloaded the Payment page while booking was in progress.\nHold: ${holdId}\nConfirmationNumber: ${confNum}`);
    }
  }

  /* User defined private/public variables */
  public booking: Booking;
  public cardDetail = new CardDetail();
  public currencyCode: string;
  public eligibility: EligibilityDetail[] = [];
  public emailResponse: EmailResponse;
  public errorMessage: any;
  public isBookingInProgress: boolean = false;
  public metricsDetail: MetricDetails[];
  public postedErrorMessage: string = '';
  public paymentOrchestratorSessionDetail: CreatePaymentSessionResponse;
  public reminderDetails = new ReminderDetails();
  public rescheduledConfirmationList: string[] = [];
  public selectedAppointments: AppointmentDetail[] = [];
  public selectedSite: Site;
  public totalAmount: number = 0.00;

  /* Service subscriptions */
  booking$: Subscription;
  eligibility$: Subscription;
  metricsDetail$: Subscription;
  paymentOrchestratorSessionDetail$: Subscription;
  selectedAppointments$: Subscription;
  translateLanguage$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private messagingApiService: MessagingApiService,
    private metricApiService: MetricApiService,
    private paymentApiService: PaymentApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private loaderService: LoaderService
  ) {
    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    this.eligibility$ = selectedExamDetailService.eligibilityDetails.subscribe(
      eligibility => {
        this.eligibility = eligibility;
      });

    this.metricsDetail$ = selectedExamDetailService.metricsDetail.subscribe(
      metricsDetail => {
        this.metricsDetail = metricsDetail;
      });

    this.paymentOrchestratorSessionDetail$ = selectedExamDetailService.paymentOrchestratorSessionDetail.subscribe(
      paymentOrchestratorSessionDetail => {
        this.paymentOrchestratorSessionDetail = paymentOrchestratorSessionDetail;
      });

    this.selectedExamDetailService.textReminder.subscribe(
      reminder => {
        if (reminder && reminder.mobileNumber && reminder.countryISDCode) {
          this.reminderDetails.notificationSelected = reminder.isNotificationSelected;
          this.reminderDetails.contactNumber = reminder.countryISDCode.concat(reminder.mobileNumber);
        }
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      }
    );
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.PaymentComponent)) { return; }
    this.totalAmount = this.selectedExamDetailService.rescheduleFee['totalAmount'];
    this.currencyCode = this.selectedExamDetailService.rescheduleFee['currency'];
    this.selectedSite = this.selectedAppointments[0].appointmentSlot.site;
    this.isBookingInProgress = false;
  }

  /* Move back a page. */
  goBack(): void {
    this.routingService.backwardNavigation(ComponentType.PaymentComponent);
  }

  /**
   * Submit
   * @param event
   */
  submit(event: any): void {
    this.isBookingInProgress = true;

    // Sort selected Appoinement list for authorization and Settlement process
    // Both are taking max confirmation number for settlement payment and authorize
    this.selectedAppointments.sort((leftSide, rightSide): number => {
      if (leftSide.ConfirmationNumber < rightSide.ConfirmationNumber) { return -1; }
      if (leftSide.ConfirmationNumber > rightSide.ConfirmationNumber) { return 1; }
      return 0;
    });

    this.redirectToPayment();
  }

  /* submit using non-SCA CC flow */
  redirectToPayment(): void {
    this.timerService.clearClock();
    this.loaderService.showActive();
    
    let paymentDetails: CreatePaymentSessionRequest = new CreatePaymentSessionRequest(
      this.totalAmount,
      this.currencyCode,
      this.booking.client.code,
      this.selectedAppointments[this.selectedAppointments.length - 1].program.code,
      this.selectedAppointments[this.selectedAppointments.length - 1].exam.code,
      CardFee.Reschedule,
      this.selectedExamDetailService.isProctored,
      !!this.selectedExamDetailService.terminationUrl ? this.selectedExamDetailService.terminationUrl : undefined
    );

    let examDetails: any[] = [];

    this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
      examDetails.push({
        'examId': appointment.exam.id,
        'amount': appointment.payments[0].amount
      });
    });

    const tests: any[] = [];
    const examDetailsForSettlement: any[] = [];
    // Set selected Exam id and hold id in test array and pass it to reschedule booking.
    // Create test request object for Request Data.
    this.selectedAppointments.forEach((selectedAppointment: AppointmentDetail) => {

      if (!!selectedAppointment.exam.sessions && selectedAppointment.exam.sessions.length > 1) {
        tests.push({
          "exam": {
            "id": selectedAppointment.exam.id
          },
          "sessions": []
        });
        selectedAppointment.exam.sessions.forEach((session: Session) => {
          if (session.selected) {
            tests[0]["sessions"].push({
              "hold": {
                "id": session.hold.id
              },
              'confirmationNumber': session.confirmationNumber
            });
          }
        })
      } else {
        tests.push({
          'exam': {
            'id': selectedAppointment.exam.id
          },
          'sessions': [{
            'hold': {
              'id': selectedAppointment.hold.id
            },
            'confirmationNumber': selectedAppointment.ConfirmationNumber
          }]
        });
      }

      // Create selected exam details Request for Settlement process
      const examFee: any = selectedAppointment.payments.filter(payment => payment.transactionType === Fee.RescheduleFee);
      examDetailsForSettlement.push({
        'confirmationNumber': selectedAppointment.ConfirmationNumber,
        'amount': examFee[0].amount
      });
    });

    let eligId = undefined;
    if (!!this.eligibility && !!this.eligibility.length && !!this.eligibility[0] && !!this.eligibility[0].id) {
      eligId = this.eligibility.filter(e => e.exam.id === this.selectedAppointments[0].exam.id)[0].id;
    }

    const reScheduleBookingRequestData = {
      'id': this.booking.id,
      'candidate': { 'id': this.booking.candidate.Id },
      'eligibility': !!eligId ? { 'id': eligId } : undefined,
      'tests': tests
    };

    reScheduleBookingRequestData['payments'] = [];

    this.selectedExamDetailService.postedConfirmationNumber = '';
    this.cardDetail.currency = this.currencyCode;

    // Final prep before redirect
    this.schedulingApiService.prepareBookingUpdate(
      paymentDetails,
      reScheduleBookingRequestData
    )
      .subscribe((prepResponse) => {
        if ((!!prepResponse && prepResponse.status) && (!!this.paymentOrchestratorSessionDetail && !!this.paymentOrchestratorSessionDetail.paymentOrchestratorUrl)) {
          this.selectedExamDetailService.setExamDetailsForSettlement(examDetails);
          this.selectedExamDetailService.ssoToken = this.bootstrapParams.Token;
          this.metricApiService.log(LogLevel.Info, 'Attempting to set session data in local storage.');
          let storageErr = this.selectedExamDetailService.saveToLocalStorage(this.bootstrapParams.sessionGuid);

          this.isBookingInProgress = false;

          if (!storageErr) {
            this.metricApiService.log(LogLevel.Info, `Successfully set local storage session data.`);
            this.routingService.navigateToUrl(this.paymentOrchestratorSessionDetail.paymentOrchestratorUrl);
          } else if (!!storageErr && !!storageErr.message.id) {
            this.metricApiService.log(LogLevel.Debug, `Error setting session data in local storage: ${JSON.stringify(storageErr.error)}`);
            this.errorMessage = storageErr.message;
            this.isBookingInProgress = false;
            this.loaderService.hideActive();

            reScheduleBookingRequestData['tests'][0]['sessions'].forEach((session: any) => {
              this.schedulingApiService.releaseHold(session.hold).subscribe(() => { });
            });
          }
        } else if (!!prepResponse.errorId) {
          this.errorMessage = {
            id: prepResponse.errorId,
            message: 'errors.error',
            description: prepResponse.message
          };
          this.isBookingInProgress = false;
          this.loaderService.hideActive();
        }
      }, (err) => {
        this.errorMessage = {
          id: SchedulingErrors.Default,
          message: 'errors.error',
          description: 'errors.thereWasAnIssue'
        };
        this.isBookingInProgress = false;
        this.loaderService.hideActive();
      });
  }

  /* Navigate away from proscheduler(to term/prometric site) */
  goToPrometric(): void {
    this.routingService.navigateAwayFromProscheduler();
  }
}
